import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const QUILL_CONFIG = {
    modules: {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['clean']
        ],
    },
    formats: ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'align']
};

const MAX_CHARS = 3000;

const Reports = () => {
    const navigate = useNavigate();
    const [state, setState] = useState({
        message: '',
        selectedUserId: '',
        selectedUserName: '',
        userSearchText: '',
        users: [],
        filteredUsers: [],
        showUserDropdown: false,
        loadingUsers: true,
        isSubmitting: false,
        submitStatus: ''
    });

    const getTextLength = useCallback((htmlString) => {
        if (!htmlString) return 0;
        return htmlString.replace(/<[^>]*>/g, '').trim().length;
    }, []);

    const { currentLength, isOverLimit, isFormValid } = useMemo(() => {
        const length = getTextLength(state.message);
        const overLimit = length > MAX_CHARS;
        const formValid = length > 0 && !overLimit && state.selectedUserId;

        return {
            currentLength: length,
            isOverLimit: overLimit,
            isFormValid: formValid
        };
    }, [state.message, state.selectedUserId, getTextLength]);

    useEffect(() => {
        if (!state.userSearchText.trim()) {
            setState(prev => ({ ...prev, filteredUsers: [], showUserDropdown: false }));
            return;
        }

        const filtered = state.users.filter(user => {
            const fullName = `${user.name} ${user.lastname}`.toLowerCase();
            return fullName.includes(state.userSearchText.toLowerCase());
        }).slice(0, 5);

        setState(prev => ({
            ...prev,
            filteredUsers: filtered,
            showUserDropdown: filtered.length > 0
        }));
    }, [state.userSearchText, state.users]);

    useEffect(() => {
        let isMounted = true;

        fetch('https://wellness.nextriking.com/api/users')
            .then(response => response.ok ? response.json() : Promise.reject())
            .then(data => {
                if (isMounted && !data.error && data.body) {
                    setState(prev => ({ ...prev, users: data.body, loadingUsers: false }));
                }
            })
            .catch(error => {
                console.error('Error al cargar usuarios:', error);
                if (isMounted) setState(prev => ({ ...prev, loadingUsers: false }));
            });

        return () => { isMounted = false; };
    }, []);

    const handleWhatsAppShare = useCallback(() => {
        if (!isFormValid) {
            alert('Debe seleccionar un usuario y escribir un mensaje válido');
            return;
        }

        const selectedUser = state.users.find(user => user.id == state.selectedUserId);
        if (!selectedUser?.phone) {
            alert('El usuario seleccionado no tiene número de teléfono');
            return;
        }

        const textMessage = getTextLength(state.message) > 0
            ? state.message.replace(/<[^>]*>/g, '').trim()
            : '';

        const cleanPhone = selectedUser.phone.replace(/[\s\-\(\)]/g, '');
        window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(textMessage)}`, '_blank');
    }, [isFormValid, state.users, state.selectedUserId, state.message, getTextLength]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!isFormValid) {
            setState(prev => ({ ...prev, submitStatus: 'error' }));
            return;
        }

        setState(prev => ({ ...prev, isSubmitting: true, submitStatus: '' }));

        try {
            // Usar la ruta del backend existente
            const response = await fetch('https://wellness.nextriking.com/api/reports', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: state.message,
                    userId: state.selectedUserId,
                    date: new Date().toISOString().split('T')[0], // formato YYYY-MM-DD
                    trainerId: 1, // o el ID del trainer si lo tienes
                    title: 'Reporte de rendimiento'
                })
            });

            const data = await response.json();
            console.log('Respuesta del servidor:', data);

            // Verificar si la respuesta fue exitosa
            const status = (response.ok && !data.error) ? 'success' : 'error';
            const newState = { isSubmitting: false, submitStatus: status };

            if (response.ok && !data.error) {
                // Limpiar el formulario solo si fue exitoso
                Object.assign(newState, {
                    message: '',
                    selectedUserId: '',
                    selectedUserName: '',
                    userSearchText: ''
                });
            }

            setState(prev => ({ ...prev, ...newState }));
        } catch (error) {
            console.error('Error al enviar el reporte:', error);
            setState(prev => ({ ...prev, isSubmitting: false, submitStatus: 'error' }));
        }
    }, [isFormValid, state.message, state.selectedUserId]);

    const handleClear = useCallback(() => {
        setState(prev => ({
            ...prev,
            message: '',
            selectedUserId: '',
            selectedUserName: '',
            userSearchText: '',
            showUserDropdown: false,
            submitStatus: ''
        }));
    }, []);

    const handleUserSearch = useCallback((e) => {
        const value = e.target.value;
        setState(prev => ({
            ...prev,
            userSearchText: value,
            selectedUserId: '',
            selectedUserName: ''
        }));
    }, []);

    const handleUserSelect = useCallback((user) => {
        setState(prev => ({
            ...prev,
            selectedUserId: user.id,
            selectedUserName: `${user.name} ${user.lastname}`,
            userSearchText: `${user.name} ${user.lastname}`,
            showUserDropdown: false
        }));
    }, []);

    const handleInputBlur = useCallback(() => {
        setTimeout(() => {
            setState(prev => ({ ...prev, showUserDropdown: false }));
        }, 150);
    }, []);

    const handleInputFocus = useCallback(() => {
        if (state.userSearchText && state.filteredUsers.length > 0) {
            setState(prev => ({ ...prev, showUserDropdown: true }));
        }
    }, [state.userSearchText, state.filteredUsers.length]);

    const handleBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/dashboard');
    }, [navigate]);

    const styles = useMemo(() => ({
        container: {
            minHeight: '100vh',
            backgroundColor: '#f5f7fa',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        header: {
            background: 'linear-gradient(135deg, #4a6eb5 0%, #5b7db1 100%)',
            color: 'white',
            padding: '20px 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        },
        card: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #e5e9f2',
            maxWidth: '900px',
            margin: '30px auto'
        },
        autocompleteContainer: {
            position: 'relative',
            width: '100%'
        },
        autocompleteInput: {
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: 'white',
            color: '#2d3748',
            outline: 'none'
        },
        dropdown: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto'
        },
        dropdownItem: {
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: '1px solid #f7fafc',
            transition: 'background-color 0.2s'
        },
        button: (disabled, variant = 'primary') => ({
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: variant === 'primary' ? 'none' : '1px solid #e2e8f0',
            backgroundColor: disabled ? '#a0aec0' :
                variant === 'whatsapp' ? '#25D366' :
                    variant === 'secondary' ? 'white' : '#4a6eb5',
            color: variant === 'secondary' ? '#718096' : 'white'
        })
    }), []);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px'
                    }}>
                        📋
                    </div>
                    <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>Reportes</h1>
                </div>
                <button
                    onClick={handleBack}
                    style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                        ← Volver atrás
                </button>
            </div>

            <div style={{ padding: '0 40px 30px' }}>
                <div style={styles.card}>
                    <div style={{
                        padding: '20px 30px',
                        borderBottom: '1px solid #e5e9f2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <div style={{
                            backgroundColor: '#f8f9fc',
                            borderRadius: '8px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px'
                        }}>
                            📋
                        </div>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
                            Crear Nuevo Reporte
                        </h2>
                    </div>

                    <div style={{ padding: '30px' }}>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '25px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '12px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4a5568'
                                }}>
                                    Usuario Destinatario
                                </label>

                                {state.loadingUsers ? (
                                    <div style={{
                                        padding: '12px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        backgroundColor: '#f7fafc',
                                        color: '#718096',
                                        fontSize: '14px'
                                    }}>
                                        Cargando usuarios...
                                    </div>
                                ) : (
                                    <div style={styles.autocompleteContainer}>
                                        <input
                                            type="text"
                                            value={state.userSearchText}
                                            onChange={handleUserSearch}
                                            onFocus={handleInputFocus}
                                            onBlur={handleInputBlur}
                                            placeholder="Escriba el nombre del usuario..."
                                            style={{
                                                ...styles.autocompleteInput,
                                                borderBottomLeftRadius: state.showUserDropdown ? '0' : '8px',
                                                borderBottomRightRadius: state.showUserDropdown ? '0' : '8px'
                                            }}
                                        />

                                        {state.showUserDropdown && (
                                            <div style={styles.dropdown}>
                                                {state.filteredUsers.map((user, index) => (
                                                    <div
                                                        key={user.id}
                                                        style={{
                                                            ...styles.dropdownItem,
                                                            backgroundColor: '#white',
                                                            borderBottom: index === state.filteredUsers.length - 1 ? 'none' : '1px solid #f7fafc'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f7fafc'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                        onClick={() => handleUserSelect(user)}
                                                    >
                                                        <div style={{ fontWeight: '500', color: '#2d3748' }}>
                                                            {user.name} {user.lastname}
                                                        </div>
                                                        {user.phone && (
                                                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                                                                📱 {user.phone}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {state.userSearchText && state.filteredUsers.length === 0 && !state.loadingUsers && (
                                            <div style={{
                                                ...styles.dropdown,
                                                padding: '12px 16px',
                                                color: '#718096',
                                                fontSize: '14px',
                                                textAlign: 'center'
                                            }}>
                                                No se encontraron usuarios
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '12px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4a5568'
                                }}>
                                    Contenido del Reporte
                                </label>

                                <div style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={state.message}
                                        onChange={(value) => setState(prev => ({ ...prev, message: value }))}
                                        modules={QUILL_CONFIG.modules}
                                        formats={QUILL_CONFIG.formats}
                                        placeholder="Escriba aquí el contenido de su reporte ..."
                                        style={{
                                            minHeight: '400px',
                                            maxHeight: '900px',
                                            backgroundColor: 'white'
                                        }} />
                                </div>

                                {/* Contador de caracteres */}
                                <div style={{
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    marginTop: '50px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div></div>
                                    <div>
                                        <span style={{
                                            color: isOverLimit ? '#e53e3e' : '#4a5568',
                                            fontWeight: '500'
                                        }}>
                                            {currentLength}/{MAX_CHARS} caracteres
                                        </span>
                                        {isOverLimit && (
                                            <span style={{
                                                color: '#e53e3e',
                                                marginLeft: '8px',
                                                fontSize: '11px'
                                            }}>
                                                ⚠️ Excede el límite
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {state.submitStatus === 'success' && (
                                <div style={{
                                    backgroundColor: '#f0fff4',
                                    color: '#22543d',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    marginBottom: '20px',
                                    border: '1px solid #9ae6b4',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span>✅</span>
                                    Reporte enviado exitosamente
                                </div>
                            )}

                            {state.submitStatus === 'error' && (
                                <div style={{
                                    backgroundColor: '#fed7d7',
                                    color: '#742a2a',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    marginBottom: '20px',
                                    border: '1px solid #feb2b2',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span>❌</span>
                                    Error al enviar el reporte. Verifique que haya seleccionado un usuario, el contenido no esté vacío y no exceda los {MAX_CHARS} caracteres.
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'space-between',
                                paddingTop: '20px',
                                borderTop: '1px solid #e5e9f2'
                            }}>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        style={styles.button(state.isSubmitting, 'secondary')}
                                        disabled={state.isSubmitting}
                                    >
                                        Limpiar
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleWhatsAppShare}
                                        disabled={!isFormValid}
                                        style={{
                                            ...styles.button(!isFormValid, 'whatsapp'),
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <span style={{ fontSize: '16px' }}>📱</span>
                                        Enviar por WhatsApp
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={state.isSubmitting || !isFormValid}
                                    style={styles.button(state.isSubmitting || !isFormValid)}
                                >
                                    {state.isSubmitting ? 'Enviando...' : 'Guardar Reporte'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reports;
